/**
 * /c/:slug/analytics/scenarios — capital-structure scenarios (CHAP-252).
 * Toggle 3-LLC (Mike Wayman) vs Brother Bonds (Colton McKinney), or compare
 * side-by-side. Defaults to the active scenario, else the default.
 *
 * CHAP-491 — merged in the underwriting bar from omega-club's
 * `CapStackComparison.jsx`, the last board surface without a ChapterBuild
 * counterpart. It is a MERGE, not a port: this page already rendered the two
 * scenarios from the database, and the board component's addition was the
 * layer that scores them against what a lender actually tests for. Porting it
 * as its own page would have left two surfaces both rendering 3-LLC vs
 * Brother Bonds, which is how the original figure drift started.
 *
 * Deliberately NOT carried over: omega-club's three frozen HISTORICAL-SCENARIO
 * cards (the Feb–May 2026 basis, NOI $2,456,311 at 6.25%). Superseded models
 * belong on Version History, which now exists and is built for exactly that;
 * duplicating them here would put retired figures on a live analysis surface
 * for no gain.
 *
 * The scoring uses each scenario's OWN sources for project cost rather than a
 * single global total, which is more honest per-stack, and the same
 * `calculatePMT` the financial projection uses (CHAP-491 lifted it into
 * shared/financial.ts) so the two surfaces cannot disagree about one loan.
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCapitalScenarios, type CapitalScenario } from '../hooks/useCapitalScenarios.js';
import { useTenantData } from '../hooks/useTenantData.js';
import { formatCurrency } from '../lib/formatters.js';
import { deriveNoiBase } from '../../shared/financial.js';
import { underwritingTests, type UnderwritingContext } from '../../shared/underwriting.js';

export default function CapitalScenarios() {
  const { slug = '' } = useParams();
  const { data, isLoading, error } = useCapitalScenarios(slug);
  const { data: tenant } = useTenantData(slug);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [compare, setCompare] = useState(false);

  const scenarios = useMemo(() => data?.scenarios ?? [], [data]);

  // The underwriting bar needs the adopted model's NOI and cap rate. Both come
  // from the primary financial model; when it is absent the bar is omitted
  // rather than scored against zeros, which would read as four failures.
  const underwriting = useMemo<UnderwritingContext | null>(() => {
    const model = tenant?.financialModels?.find((m) => m.isPrimary) ?? tenant?.financialModels?.[0];
    if (!model) return null;
    const noi = deriveNoiBase({
      beds: model.beds,
      rentPerBedMonthly: model.rentPerBedMonthly,
      noi: model.noi,
    });
    const capRatePct = Number(model.capRate ?? 0);
    if (!noi || !capRatePct) return null;
    return { noi, capRatePct, termYears: model.termYears ?? 30 };
  }, [tenant]);
  /**
   * CHAP-330: the scenario source amounts are a frozen pre-v2 comparison. The
   * seed says so (HISTORICAL-SCENARIO, "the source rows are intentionally
   * frozen"), but that is a code comment and no reader sees it - so this page
   * rendered a $43.1M total with nothing to distinguish it from the adopted  HISTORICAL-SCENARIO: the retired total, named to explain the note
   * $47.67M one click away on Summary. That is the confusion this note exists
   * to remove.
   *
   * Derived rather than hardcoded: compare each scenario's own sources against
   * the adopted project cost. If the scenarios are ever re-costed on the
   * adopted basis, the note disappears by itself instead of becoming the next
   * stale label.
   */
  const adoptedTotal = useMemo(() => {
    const tile = tenant?.kpis?.find((k) => k.label === 'Total Project Cost');
    return typeof tile?.valueNumeric === 'number' ? tile.valueNumeric : null;
  }, [tenant]);
  const offBasis = useMemo(
    () => (adoptedTotal === null ? [] : scenarios.filter((s) => s.total !== adoptedTotal)),
    [scenarios, adoptedTotal],
  );

  const selected = useMemo(() => {
    if (selectedKey) return scenarios.find((s) => s.scenarioKey === selectedKey) ?? scenarios[0];
    return scenarios.find((s) => s.isActive) ?? scenarios.find((s) => s.isDefault) ?? scenarios[0];
  }, [scenarios, selectedKey]);

  if (isLoading) return <div className="p-8 text-center text-sm text-neutral">Loading scenarios…</div>;
  if (error) return <div className="p-8 text-center text-sm text-red-700">{error.message}</div>;
  if (scenarios.length === 0) return <div className="p-8 text-center text-sm text-neutral">No capital-structure scenarios seeded.</div>;

  return (
    <div className="space-y-4 min-w-0">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 flex items-start justify-between flex-wrap gap-3 min-w-0">
        <div className="min-w-0">
          <h2 className="text-navy text-xl sm:text-2xl font-bold">Capital Structure</h2>
          <p className="text-neutral text-sm mt-1">Compare the financing models under consideration.</p>
        </div>
        <button type="button" onClick={() => setCompare((c) => !c)} className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 flex-shrink-0">
          {compare ? 'Single view' : 'Compare side-by-side'}
        </button>
      </header>

      {!compare && (
        <div className="flex flex-wrap gap-2 min-w-0">
          {scenarios.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSelectedKey(s.scenarioKey)}
              className={`text-sm px-3 py-1.5 rounded-lg border ${selected?.scenarioKey === s.scenarioKey ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-200 hover:border-navy/40'}`}
            >
              {s.name}
              {s.advocate && <span className="opacity-70"> · {s.advocate}</span>}
              {s.isActive && <span className="ml-1 text-[10px] bg-green-500/20 text-green-100 px-1 rounded">adopted</span>}
            </button>
          ))}
        </div>
      )}

      {adoptedTotal !== null && offBasis.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 min-w-0">
          <strong>These structures are costed on an earlier basis.</strong>{' '}
          {offBasis.length === scenarios.length
            ? 'Every scenario below'
            : `${offBasis.length} of ${scenarios.length} scenarios below`}{' '}
          sums to a project cost other than the adopted {formatCurrency(adoptedTotal)}. They are kept to
          compare <em>how the money is raised</em>, not what the project costs. Re-costing them on the
          adopted basis is a modelling decision for the cabinet.
        </div>
      )}

      {compare ? (
        <div className="grid lg:grid-cols-2 gap-3 min-w-0">
          {scenarios.map((s) => <ScenarioCard key={s.id} s={s} u={underwriting} />)}
        </div>
      ) : (
        selected && <ScenarioCard s={selected} u={underwriting} />
      )}

      <div className="bg-navy/5 rounded-lg p-3 text-xs text-neutral">
        Cabinet decides via a vote. See{' '}
        <Link to={`/c/${slug}/voting`} className="text-blue-600 underline">Voting</Link>
        {' '}- "Capital structure: 3-LLC vs Brother Bonds". The adopted scenario is marked here.
      </div>
    </div>
  );
}

const TYPE_LABEL: Record<string, string> = {
  gift: 'Gift', 'llc-equity': 'LLC equity', 'senior-debt': 'Senior debt',
  'subordinate-debt': 'Subordinate debt', mezzanine: 'Mezzanine', equity: 'Equity', reserve: 'Reserve',
};

function ScenarioCard({ s, u }: { s: CapitalScenario; u: UnderwritingContext | null }) {
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 min-w-0">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="min-w-0">
          <h3 className="text-navy font-bold text-sm sm:text-base">{s.name}</h3>
          {s.advocate && <p className="text-xs text-neutral">Championed by {s.advocate}</p>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {s.complexity && <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.complexity === 'high' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>{s.complexity} complexity</span>}
          {s.isActive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">Adopted</span>}
          {s.isDefault && !s.isActive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">Default</span>}
        </div>
      </div>

      <div className="min-w-0 overflow-x-auto">
        <table className="w-full text-sm">
          <tbody>
            {s.sources.map((src, i) => (
              <tr key={i} className="border-b border-gray-50">
                <td className="py-1 pr-2"><span className="text-navy">{src.name}</span>
                  {src.taxDeductible && <span className="ml-1 text-[10px] text-green-700">tax-deductible</span>}
                  {src.k1 && <span className="ml-1 text-[10px] text-amber-700">K-1</span>}
                  {src.interestRate != null && <span className="ml-1 text-[10px] text-neutral">{src.interestRate}%</span>}
                </td>
                <td className="py-1 pr-2 text-xs text-neutral whitespace-nowrap">{TYPE_LABEL[src.type] ?? src.type}</td>
                <td className="py-1 text-right font-mono whitespace-nowrap">{formatCurrency(src.amount)}</td>
              </tr>
            ))}
            <tr className="font-bold text-navy"><td className="pt-1.5">Total</td><td /><td className="pt-1.5 text-right font-mono">{formatCurrency(s.total)}</td></tr>
          </tbody>
        </table>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-3 min-w-0">
        <div>
          <p className="text-xs font-semibold text-green-800 mb-1">Pros</p>
          <ul className="space-y-0.5">{s.pros.map((p, i) => <li key={i} className="text-xs text-gray-700">+ {p}</li>)}</ul>
        </div>
        <div>
          <p className="text-xs font-semibold text-red-800 mb-1">Cons</p>
          <ul className="space-y-0.5">{s.cons.map((c, i) => <li key={i} className="text-xs text-gray-700">- {c}</li>)}</ul>
        </div>
      </div>

      {u && <UnderwritingBar s={s} u={u} />}
    </section>
  );
}


/**
 * The underwriting bar. Scoring lives in `shared/underwriting.ts` so it can be
 * tested without rendering — see `underwriting.test.ts`, which scores both of
 * Omega Zeta's real stacks against the seeded figures.
 */
function UnderwritingBar({ s, u }: { s: CapitalScenario; u: UnderwritingContext }) {
  const tests = underwritingTests(s.sources, s.total, u);
  const failures = tests.filter((x) => !x.pass).length;
  return (
    <div className="mt-3 border-t border-gray-100 pt-3 min-w-0">
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
        <p className="text-xs font-semibold text-navy">Underwriting bar &mdash; what lenders test for</p>
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full ${failures === 0 ? 'bg-green-100 text-green-800' : failures === 1 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}
        >
          {failures === 0 ? 'clears all four' : `${failures} of 4 short`}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 min-w-0">
        {tests.map((x) => (
          <div key={x.label} className="min-w-0">
            <p className="text-[10px] text-neutral truncate">{x.label}</p>
            <p className={`font-mono text-sm ${x.pass ? 'text-green-700' : 'text-red-700'}`}>{x.value}</p>
            <p className="text-[10px] text-neutral">{x.threshold}</p>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-neutral mt-2">
        Scored against this scenario&rsquo;s own sources and the adopted model&rsquo;s NOI and cap
        rate. A shortfall is not a verdict &mdash; it is the conversation to have with the lender.
      </p>
    </div>
  );
}
